import type { BoothChapter, BoothStepId } from '../types/booth';

// CREATIVE DEMONSTRATION ONLY. All product statements are draft wording, carry reference
// placeholders and are subject to Ferring medical, legal and regulatory review.

export const boothMedia = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/f34cabdc-ecb0-4f62-b04a-3eb8964bd76e.jpg',
};

export const boothSteps: { id: BoothStepId; label: string }[] = [
  { id: 'question', label: 'Question' },
  { id: 'science', label: 'Science' },
  { id: 'explore', label: 'Explore' },
  { id: 'takeaway', label: 'Takeaway' },
];

export const selfHints: Record<BoothStepId, string> = {
  question: 'Tap the answer closest to your own practice.',
  science: 'Read the key points, then continue to explore.',
  explore: 'Interact with the visual to explore.',
  takeaway: 'Choose another chapter or finish your visit.',
};

export const boothChapters: BoothChapter[] = [
  {
    id: 'dosing',
    number: '01',
    title: 'Individualised dosing',
    kicker: 'The starting dose',
    question: 'How do you currently choose a starting dose for ovarian stimulation?',
    pollOptions: ['Mainly AMH or AFC', 'Age and body weight', 'A standard dose, then adjust', 'Previous response'],
    pollBridge: 'Every approach starts from a different patient factor. Let’s look at how REKOVELLE dosing is determined.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6e52f2ee-f182-479b-b3e0-4ec5978f2e37.jpg',
    accent: '#0F9C8C',
    scienceTitle: 'Dosing built on two patient factors',
    points: [
      {
        title: 'AMH and body weight',
        body: 'The REKOVELLE daily dose is determined from the patient’s serum AMH and body weight, using the dosing algorithm in the Summary of Product Characteristics. [Ref]',
      },
      {
        title: 'A fixed daily dose',
        body: 'The individualised daily dose is maintained throughout stimulation, as described in the SmPC. [Ref]',
      },
      {
        title: 'Dosed in micrograms',
        body: 'REKOVELLE is dosed in µg rather than IU and is not interchangeable unit-for-unit with other gonadotropins. [Ref]',
      },
    ],
    exploreTitle: 'Two inputs. One individualised starting point.',
    exploreIntro: 'Move the sliders to see how the two patient factors feed into the dosing approach.',
    interactive: 'dosing',
    takeaway: 'One algorithm. Two patient factors. An individualised starting point.',
    repPrompts: {
      question: 'Ask: “How do you usually decide on a starting dose?” Let them answer before revealing anything.',
      science: 'Listen for AMH or body weight — use their answer to bridge to the two-factor algorithm.',
      explore: 'Hand over the iPad and invite them to move the sliders themselves.',
      takeaway: 'Summarise in one sentence, then ask which subject they’d like to explore next.',
    },
  },
  {
    id: 'molecule',
    number: '02',
    title: 'The molecule',
    kicker: 'Origin and structure',
    question: 'Does the cell line used to produce recombinant FSH matter to you?',
    pollOptions: ['Yes, significantly', 'Somewhat', 'Not particularly', 'I’d like to know more'],
    pollBridge: 'The cell line is where a recombinant molecule’s structure begins. Let’s look closer.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d751efbf-95f4-4fb2-b059-7197d4427977.jpg',
    accent: '#5468D8',
    scienceTitle: 'A human cell line-derived rFSH',
    points: [
      {
        title: 'Human cell line origin',
        body: 'Follitropin delta is produced in a human cell line (PER.C6®), whereas other recombinant FSH products are produced in Chinese hamster ovary (CHO) cells. [Ref]',
      },
      {
        title: 'A different glycosylation profile',
        body: 'Production in a human cell line results in a sialylation profile that includes both α2,3- and α2,6-linked sialic acid. [Ref]',
      },
      {
        title: 'Its own pharmacokinetic profile',
        body: 'Follitropin delta has a pharmacokinetic profile that differs from follitropin alfa, which is reflected in its dosing. [Ref]',
      },
    ],
    exploreTitle: 'Compare the cell lines',
    exploreIntro: 'Switch between cell lines to see how the terminal sialic acid linkages differ. Schematic, not to scale.',
    interactive: 'molecule',
    takeaway: 'The source of a molecule shapes its structure.',
    repPrompts: {
      question: 'Ask: “Have you ever considered where a recombinant FSH molecule is made?”',
      science: 'Keep it visual: the cell line determines the glycosylation profile. Avoid efficacy claims here.',
      explore: 'Let them toggle between cell lines — point out the α2,6-linked sialic acid.',
      takeaway: 'Bridge: “Would it be useful to see how this was studied clinically?”',
    },
  },
  {
    id: 'evidence',
    number: '03',
    title: 'Clinical evidence',
    kicker: 'Balancing response',
    question: 'When you assess a stimulation trial, which outcome matters most to you?',
    pollOptions: ['Ongoing pregnancy', 'Oocytes retrieved', 'Excessive response and safety', 'Treatment burden'],
    pollBridge: 'Different outcomes matter to different specialists. Here is how the phase 3 programme was designed.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b5d35541-63a5-46a5-8bf3-e3f77d35409b.jpg',
    accent: '#D9654F',
    scienceTitle: 'A randomised, controlled phase 3 programme',
    points: [
      {
        title: 'Study design',
        body: 'REKOVELLE with individualised dosing was evaluated in randomised controlled trials versus follitropin alfa with conventional dosing. [Ref]',
      },
      {
        title: 'Efficacy outcomes',
        body: '[Insert approved primary endpoint wording and results]',
        placeholder: true,
      },
      {
        title: 'Response and safety outcomes',
        body: '[Insert approved wording on ovarian response and safety endpoints]',
        placeholder: true,
      },
    ],
    exploreTitle: 'Explore the trial design',
    exploreIntro: 'Tap each stage of the study to reveal more.',
    interactive: 'evidence',
    takeaway: 'Evidence to consider alongside your own clinical judgement.',
    repPrompts: {
      question: 'Ask which outcome matters most to them — then open the stage that matches their answer.',
      science: 'Use only approved results. Do not paraphrase or extrapolate beyond the approved wording.',
      explore: 'Walk through population → randomisation → arms → endpoints at their pace.',
      takeaway: 'Offer the full publication or medical information request if they want more detail.',
    },
  },
  {
    id: 'pen',
    number: '04',
    title: 'Practical treatment',
    kicker: 'The patient’s experience',
    question: 'What creates the most friction for patients during stimulation?',
    pollOptions: ['Preparing injections', 'Changing doses', 'Clinic visits', 'Anxiety and confidence'],
    pollBridge: 'Practical details shape how patients experience treatment. Here is how REKOVELLE is administered.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6118b907-6b8a-47f6-9e07-2b49ac16d0fa.jpg',
    accent: '#7A5AC8',
    scienceTitle: 'Designed for daily self-administration',
    points: [
      {
        title: 'Prefilled pen',
        body: 'REKOVELLE is supplied in a prefilled, multi-dose injection pen. [Ref]',
      },
      {
        title: 'Dial the dose',
        body: 'The prescribed dose is set using the dose dial on the pen. [Ref]',
      },
      {
        title: 'Patient support',
        body: '[Insert locally approved patient training and support materials]',
        placeholder: true,
      },
    ],
    exploreTitle: 'The injection, step by step',
    exploreIntro: 'Tap through the main steps. Illustrative — always refer to the Instructions for Use.',
    interactive: 'pen',
    takeaway: 'Practical details matter to patients.',
    repPrompts: {
      question: 'Ask: “What do your patients find hardest about injecting at home?”',
      science: 'Keep it factual: prefilled, multi-dose pen with a dose dial.',
      explore: 'Tap through the steps together — offer a demo pen if available at the booth.',
      takeaway: 'Ask if their nurses would value patient training materials.',
    },
  },
];

export const boothDisclaimer =
  'Creative demonstration — not approved promotional material. Product statements are draft wording with reference placeholders, subject to Ferring medical, legal and regulatory review. Prescribing information to be added.';
